// import React, { useState, useEffect, useRef } from "react";
// import { createPortal } from "react-dom";
// import { motion, AnimatePresence } from "framer-motion";

// const DropdownMenuNew = ({ children, MenuSection }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [position, setPosition] = useState({ top: 0, left: 0 });
//   const [isRightAligned, setIsRightAligned] = useState(false);
//   const dropdownRef = useRef(null);
//   const toggleRef = useRef(null);

//   const toggleDropdown = () => setIsOpen((prev) => !prev);
//   const closeDropdown = () => setIsOpen(false);

//   // Calculate portal position
//   useEffect(() => {
//     if (isOpen && toggleRef.current) {
//       const rect = toggleRef.current.getBoundingClientRect();
//       const dropdownWidth = 150; // min-w-[150px]
//       const dropdownHeight = 200; // estimated height

//       const viewportWidth = window.innerWidth;
//       const viewportHeight = window.innerHeight;

//       const rightSpace = viewportWidth - rect.right;
//       const bottomSpace = viewportHeight - rect.bottom;

//       const top =
//         bottomSpace >= dropdownHeight
//           ? rect.bottom + window.scrollY
//           : rect.top + window.scrollY - dropdownHeight;
//       const left =
//         rightSpace >= dropdownWidth
//           ? rect.left + window.scrollX
//           : rect.right + window.scrollX - dropdownWidth;

//       setPosition({ top, left });
//       setIsRightAligned(rightSpace < dropdownWidth);
//     }
//   }, [isOpen]);

//   // Close on outside click
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (
//         dropdownRef.current &&
//         !dropdownRef.current.contains(e.target) &&
//         !toggleRef.current.contains(e.target)
//       ) {
//         closeDropdown();
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // Close on Escape
//   useEffect(() => {
//     const handleEscape = (e) => e.key === "Escape" && closeDropdown();
//     window.addEventListener("keydown", handleEscape);
//     return () => window.removeEventListener("keydown", handleEscape);
//   }, []);

//   const dropdownVariants = {
//     hidden: { opacity: 0, y: -10, scale: 0.95 },
//     visible: {
//       opacity: 1,
//       y: 0,
//       scale: 1,
//       transition: { duration: 0.2, ease: "easeOut" },
//     },
//     exit: {
//       opacity: 0,
//       y: -10,
//       scale: 0.95,
//       transition: { duration: 0.15, ease: "easeIn" },
//     },
//   };

//   return (
//     <>
//       {/* Toggle Button */}
//       <div
//         ref={toggleRef}
//         onClick={toggleDropdown}
//         className="cursor-pointer"
//         role="button"
//         aria-expanded={isOpen}
//         aria-haspopup="true"
//       >
//         {React.isValidElement(children)
//           ? React.cloneElement(children, { active: isOpen })
//           : children}
//       </div>

//       {/* Portal Dropdown */}
//       {typeof document !== "undefined" &&
//         createPortal(
//           <AnimatePresence>
//             {isOpen && (
//               <motion.div
//                 ref={dropdownRef}
//                 className="absolute bg-white shadow-md rounded-md p-1 z-55 min-w-[150px]"
//                 style={{ top: position.top, left: position.left }}
//                 initial="hidden"
//                 animate="visible"
//                 exit="exit"
//                 variants={dropdownVariants}
//                 aria-hidden={!isOpen}
//               >
//                 {typeof MenuSection === "function" ? (
//                   <MenuSection closeDropdown={closeDropdown} />
//                 ) : (
//                   React.Children.map(MenuSection, (child) =>
//                     React.isValidElement(child)
//                       ? React.cloneElement(child, {
//                           onClick: (e) => {
//                             child.props.onClick?.(e);
//                             closeDropdown();
//                           },
//                         })
//                       : child,
//                   )
//                 )}
//               </motion.div>
//             )}
//           </AnimatePresence>,
//           document.body,
//         )}
//     </>
//   );
// };

// export default DropdownMenuNew;

import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

const DropdownMenuNew = ({ children, MenuSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);
  const closeDropdown = () => setIsOpen(false);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const clamp = (value, min, max) => {
      if (max < min) return min;
      return Math.min(Math.max(value, min), max);
    };

    const updatePosition = () => {
      if (!toggleRef.current || !dropdownRef.current) return;

      const rect = toggleRef.current.getBoundingClientRect();
      const menuEl = dropdownRef.current;

      const dropdownWidth =
        menuEl.offsetWidth || menuEl.getBoundingClientRect().width || 0;
      const dropdownHeight =
        menuEl.offsetHeight || menuEl.getBoundingClientRect().height || 0;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8;

      const fitsRight = rect.left + dropdownWidth <= viewportWidth - margin;
      const fitsLeft = rect.right - dropdownWidth >= margin;
      const fitsBelow = rect.bottom + dropdownHeight <= viewportHeight - margin;
      const fitsAbove = rect.top - dropdownHeight >= margin;

      const leftViewport =
        !fitsRight && fitsLeft ? rect.right - dropdownWidth : rect.left;
      const topViewport =
        !fitsBelow && fitsAbove ? rect.top - dropdownHeight : rect.bottom;

      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      const minLeft = scrollX + margin;
      const maxLeft = scrollX + viewportWidth - dropdownWidth - margin;
      const minTop = scrollY + margin;
      const maxTop = scrollY + viewportHeight - dropdownHeight - margin;

      const left = clamp(scrollX + leftViewport, minLeft, maxLeft);
      const top = clamp(scrollY + topViewport, minTop, maxTop);

      setPosition({ top, left });
    };

    let raf1 = 0;
    let raf2 = 0;

    raf1 = window.requestAnimationFrame(() => {
      updatePosition();
      raf2 = window.requestAnimationFrame(updatePosition);
    });

    window.addEventListener("resize", updatePosition);
    window.addEventListener("orientationchange", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("orientationchange", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => e.key === "Escape" && closeDropdown();
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.15, ease: "easeIn" },
    },
  };

  return (
    <>
      {/* Toggle Button */}
      <div
        ref={toggleRef}
        onClick={toggleDropdown}
        className="cursor-pointer"
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {React.isValidElement(children)
          ? React.cloneElement(children, { active: isOpen })
          : children}
      </div>

      {/* Portal Dropdown */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                ref={dropdownRef}
                className="absolute bg-white shadow-md rounded-md p-1 z-55 min-w-[150px] max-w-[calc(100vw-16px)] max-h-[calc(100vh-16px)] overflow-auto"
                style={{ top: position.top, left: position.left }}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={dropdownVariants}
                aria-hidden={!isOpen}
              >
                {typeof MenuSection === "function" ? (
                  <MenuSection closeDropdown={closeDropdown} />
                ) : (
                  React.Children.map(MenuSection, (child) =>
                    React.isValidElement(child)
                      ? React.cloneElement(child, {
                          onClick: (e) => {
                            child.props.onClick?.(e);
                            closeDropdown();
                          },
                        })
                      : child,
                  )
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
};

export default DropdownMenuNew;
